import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { attachmentStorageKey } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import type { BlobRow } from '../sync/db.ts'
import { Banner, Pill } from '../ui/components.tsx'
import type { PreparedPhoto } from './capture.ts'
import { useBlobs, useEntityPhotos, usePhotosWaiting, type LinkRow, type PhotoDraft } from './usePhotos.ts'

const THUMB = 'size-20 rounded-lg border-2 border-slate-300 object-cover dark:border-slate-600'

/** The camera button and the pictures taken so far. The native camera sheet (`capture`) is used on purpose: no getUserMedia, which is unreliable in an installed iPhone app. */
export function PhotoField({ draft, label, disabled }: { draft: PhotoDraft; label?: string; disabled?: boolean }) {
  const { t } = useTranslation()
  const input = useRef<HTMLInputElement>(null)
  return (
    <div className="flex flex-col gap-2" data-testid="photo-field">
      {draft.photos.length > 0 && <ul className="flex flex-wrap gap-2" data-testid="photo-draft">{draft.photos.map((p) => <li key={p.sha256}><DraftThumb photo={p} onRemove={() => draft.remove(p.sha256)} /></li>)}</ul>}
      <button type="button" className="btn" disabled={draft.busy || disabled} onClick={() => input.current?.click()} data-testid="photo-add">{draft.busy ? t('photos.preparing') : (label ?? t('photos.add'))}</button>
      <input ref={input} type="file" accept="image/*" capture="environment" className="sr-only" tabIndex={-1} aria-hidden data-testid="photo-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void draft.addFile(f) }} />
      {draft.error && <Banner tone="warn"><span data-testid="photo-error">{draft.error}</span></Banner>}
    </div>
  )
}

function DraftThumb({ photo, onRemove }: { photo: PreparedPhoto; onRemove(): void }) {
  const { t } = useTranslation()
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => { const u = URL.createObjectURL(photo.blob); setUrl(u); return () => URL.revokeObjectURL(u) }, [photo.blob])
  return (
    <span className="relative inline-block">
      {url && <img src={url} alt={t('photos.alt')} className={THUMB} data-testid="photo-thumb" />}
      <button type="button" className="absolute -right-2 -top-2 flex size-7 items-center justify-center rounded-full bg-red-700 text-sm font-bold text-white" onClick={onRemove} aria-label={t('photos.remove')} data-testid="photo-remove">×</button>
    </span>
  )
}

/** The pictures that are evidence for a record, from this device's copy while the server does not have the bytes yet and from storage afterwards. */
export function PhotoStrip({ entityId }: { entityId: string }) {
  const { t } = useTranslation()
  const links = useEntityPhotos(entityId)
  const blobs = useBlobs()
  if (links.length === 0) return null
  return (
    <ul className="flex flex-wrap gap-2" data-testid="photo-strip" data-count={links.length}>
      {links.map((l) => <li key={l.id}><LinkThumb link={l} blob={blobs.get(l.sha256)} label={t('photos.alt')} /></li>)}
    </ul>
  )
}

function LinkThumb({ link, blob, label }: { link: LinkRow; blob: BlobRow | undefined; label: string }) {
  const { t } = useTranslation()
  const { transport, org } = useClub()
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let live = true; let revoke: string | null = null
    if (blob) { revoke = URL.createObjectURL(blob.blob); setUrl(revoke) }
    else {
      setUrl(null)
      void transport.attachmentUrl(attachmentStorageKey(org.org_id, link.site_id, link.sha256, link.mime)).then((u) => { if (live) setUrl(u) }).catch(() => { /* offline, or not stored yet: the placeholder stays */ })
    }
    return () => { live = false; if (revoke) URL.revokeObjectURL(revoke) }
  }, [blob, link.sha256, link.mime, link.site_id, transport, org.org_id])
  return (
    <span className="relative inline-flex flex-col items-start gap-1" data-sha={link.sha256}>
      {url ? <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={link.caption ?? label} className={THUMB} data-testid="photo-thumb" /></a>
           : <span className={`${THUMB} flex items-center justify-center bg-slate-100 p-1 text-center text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300`} data-testid="photo-placeholder">{t('photos.unavailable')}</span>}
      {blob && <Pill tone={blob.state === 'failed' ? 'red' : 'amber'}><span data-testid="photo-pill">{blob.state === 'failed' ? t('photos.failed') : t('photos.waiting')}</span></Pill>}
    </span>
  )
}

/** "2 photos waiting to be sent": quiet when there is nothing to say. */
export function PhotosWaiting() {
  const { t } = useTranslation()
  const { waiting, failed } = usePhotosWaiting()
  if (waiting + failed === 0) return null
  return (
    <div className="flex flex-col gap-1" data-testid="photos-waiting">
      {waiting > 0 && <Pill tone="amber"><span data-testid="photos-waiting-count">{t('photos.waitingCount', { count: waiting })}</span></Pill>}
      {failed > 0 && <Pill tone="red">{t('photos.failedCount', { count: failed })}</Pill>}
    </div>
  )
}
